import React, { useState, useContext, useEffect } from "react";
import { Badge, Select, Box, Button, Skeleton, Checkbox, Divider, HStack, Icon, IconButton, Input, InputGroup, InputLeftElement, List, ListItem, Stack, Text, useBreakpointValue, useColorModeValue as mode, VStack, } from "@chakra-ui/react";
import { FiEdit2, FiSearch, FiTrash2, FiPlay, FiRefreshCw, FiPlusSquare, FiFolder, FiPlus, FiLayers, FiCheckSquare } from "react-icons/fi";
import { useNavigate  } from "react-router-dom";
import { AppContext } from "../../App";
import { IApiSafetyChecklistResponse } from "../../models/api/safetychecklist/IApiSafetyChecklistResponse";
import { ApiContext } from "../../contexts/ApiProvider";
import { ResponsiveTable } from "../../components/ResponsiveTable/ResponsiveTable";
import toast from "react-hot-toast";
import { AddChecklistDrawer } from "../../components/drawers/checklist/AddChecklistDrawer";
import { EditChecklistDrawer } from "../../components/drawers/checklist/EditChecklistDrawer";
import { IApiSafetyChecklistEditRequest } from "../../models/api/safetychecklist/IApiSafetyChecklistEditRequest";
import Helpers from "../../Helpers";
import { AlgoliaTable } from "../../components/AlgoliaTable/AlgoliaTable";

interface IChecklistsProps {

}

export const Checklists: React.FunctionComponent<IChecklistsProps> = (props: IChecklistsProps) => {

    const navigate = useNavigate();
    const { me, setAppShellConfig } = useContext(AppContext);
    const { postSafetyChecklist, patchSafetyChecklist, deleteSafetyChecklist } = useContext(ApiContext);

    const addState = useState(false);
    const [add, setAdd] = addState;

    useEffect(() => {
        setAppShellConfig({
            breadcrumbs: [{
                text: `Vorlagen`,
                icon: FiLayers,
            },
            {
                text: `Checklisten`,
                icon: FiCheckSquare
            }],
            rightButton: {
                text: "Erstellen",
                icon: FiPlus,
                ariaLabel: "erstellen",
                variant: "primary",
                onClick: () => {
                    setAdd(true);
                }
            }
        });
    }, [setAppShellConfig, me, navigate, setAdd]);

    return (
        <Stack spacing="4" height="full" direction="column">
            <AlgoliaTable<IApiSafetyChecklistResponse> 
                indexName="dev_checklists"
                getColumns={(columnHelper, setEdit, deleteAction) => [
                    columnHelper.accessor('name', {
                        header: () => 'Name',
                        cell: info => info.getValue(),
                    }),
                    columnHelper.accessor('description', {
                        header: () => 'Beschreibung',
                        cell: info => info.getValue(),
                    }),
                    columnHelper.display({
                        id: 'actions',
                        cell: info => {
                            const checklist = info.row.original;
                            return (
                                <HStack spacing="0" justifyContent="end">
                                    <IconButton
                                        icon={<FiEdit2 fontSize="1.125rem" />}
                                        variant="ghost"
                                        aria-label="Bearbeiten"
                                        onClick={(e) => { 
                                            e.stopPropagation(); 
                                            setEdit(checklist);
                                        }}
                                    />
                                    <IconButton
                                        icon={<FiTrash2 fontSize="1.125rem" />}
                                        variant="ghost"
                                        aria-label="Löschen"
                                        onClick={(e) => { 
                                            e.stopPropagation();
                                            deleteAction(checklist);
                                        }}
                                    />                           
                                </HStack>
                            );
                        },
                    }),
                ]}
                getCard={(checklist, setEdit, deleteAction) => ({
                    key: checklist.id,
                    value: checklist.id,
                    content: (
                        <HStack spacing="4" justifyContent="space-between">
                            <VStack spacing="2" align="left">
                                <Text fontWeight="medium" fontSize={{ base: "sm", md: "md" }}>
                                    {checklist.name}
                                </Text>
                            </VStack>
                            <HStack spacing="4" justifyContent="end">
                                <IconButton
                                    icon={<FiEdit2 fontSize="1.25rem" />}
                                    variant="ghost"
                                    aria-label="Bearbeiten"
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        setEdit(checklist);
                                    }}
                                />
                                <IconButton
                                    icon={<FiTrash2 fontSize="1.25rem" />}
                                    variant="ghost"
                                    aria-label="Löschen"
                                    onClick={(e) => { 
                                        e.stopPropagation(); 
                                        deleteAction(checklist);
                                    }}
                                />
                            </HStack>
                        </HStack>
                    )
                })}
                deleteAction={(item, deleteItem) => ({
                    title: `Gewerk löschen`,
                    body: `Sind Sie sicher, dass dieses Gewerk gelöscht werden soll?`,
                    onConfirm: () => {
                        var promise = deleteSafetyChecklist(item.id);
                        toast.promise(promise, {
                            loading: `Gewerk wird gelöscht...`,
                            success: (result) => {
                                deleteItem(result);
                                return `Das Gewerk wurde erfolgreich gelöscht!`;
                            },
                            error: (code) => Helpers.buildToastErrorMessage(code, `Fehler beim Löschen des Gewerks!`)
                        });
                        return promise;
                    }
                })}
                addState={addState}
                getAddDrawer={([state, setState], addItem) => (
                    <AddChecklistDrawer 
                        isOpen={state} 
                        onDiscard={() => setState(false)}
                        onSubmit={(data) => {
                            var promise = postSafetyChecklist(data);
                            toast.promise(promise, {
                                loading: `Gewerk wird erstellt...`,
                                success: (result) => {
                                    setState(false);
                                    addItem(result);
                                    return `Das Gewerk wurde erfolgreich erstellt!`;
                                },
                                error: (code) => Helpers.buildToastErrorMessage(code, `Fehler beim Erstellen des Gewerks!`)
                            });
                            return promise;
                        }}
                    />
                )}
                getEditDrawer={([state, setState], replaceItem) => (
                    <EditChecklistDrawer 
                        defaultValue={state as IApiSafetyChecklistEditRequest} 
                        onDiscard={() => setState(undefined)} 
                        onSubmit={(data) => {
                            var promise = patchSafetyChecklist(state!.id, data);
                            toast.promise(promise, {
                                loading: `Gewerk wird aktualisiert...`,
                                success: (result) => {
                                    setState(undefined);
                                    replaceItem(result)
                                    return `Das Gewerk wurde erfolgreich aktualisiert!`;
                                },
                                error: (code) => Helpers.buildToastErrorMessage(code, `Fehler beim Aktualisieren des Gewerks!`)
                            });
                            return promise;
                        }}
                    />
                )}
            />
        </Stack>
    );
}